import { useState, useRef, useEffect, useCallback } from 'react'
import { Play, Pause, Volume2, VolumeX, X } from 'lucide-react'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
import { fmtTime } from '../lib/format'

/**
 * Fullscreen "watch" popup for a submitted (delivered) video on the Hired
 * Creators page. Deliberately separate from CreatorVideoThumbs' ThumbVideoModal
 * (used for creator top_video_urls) even though the two look identical —
 * submitted videos carry a different data shape (video_storage_url /
 * thumbnail_url / tiktok_video_url straight off cr_video_submissions), and
 * keeping them as two components lets either evolve without touching the
 * other's call sites.
 */

export default function SubmittedVideoModal({
  videoStorageUrl, thumbnailUrl, tiktokVideoUrl, onClose,
}: {
  /** Durable R2 URL — required; callers only open this modal once it's set. */
  videoStorageUrl: string
  thumbnailUrl?: string | null
  tiktokVideoUrl: string
  onClose: () => void
}) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(false)
  const [currentTime, setCurrentTime] = useState(0)
  const [duration, setDuration] = useState(0)
  const [dragging, setDragging] = useState(false)

  // Two independent signals can each decide "this video isn't playable" —
  // the muted-retry chain below AND the <video> element's native onError —
  // and under React StrictMode's dev-only double-invoke, the mount effect
  // itself can run twice. Without a guard, any combination of those fires
  // multiple `window.open` calls (reported: 3 TikTok tabs from one failure).
  // A ref-backed idempotent redirect makes only the FIRST failure signal
  // actually open a tab and close the modal; every later one is a no-op.
  const redirectedRef = useRef(false)
  const fallbackToTikTok = useCallback(() => {
    if (redirectedRef.current) return
    redirectedRef.current = true
    window.open(tiktokVideoUrl, '_blank', 'noopener,noreferrer')
    onClose()
  }, [tiktokVideoUrl, onClose])

  // Autoplay on mount. Retry muted (which autoplay policies always allow) if
  // the initial unmuted attempt is rejected, before giving up and redirecting
  // to TikTok — so the video never gets stuck silently paused.
  useEffect(() => {
    const vid = videoRef.current
    if (!vid) return
    vid.play().catch(() => {
      vid.muted = true
      setMuted(true)
      vid.play().catch(() => {
        fallbackToTikTok()
      })
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // If the mirrored file fails to load, fall back to the TikTok page.
  const handleVideoError = useCallback(() => {
    fallbackToTikTok()
  }, [fallbackToTikTok])

  useBodyScrollLock()
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  useEffect(() => {
    const vid = videoRef.current
    if (!vid) return
    const onTime = () => { if (!draggingRef.current) setCurrentTime(vid.currentTime) }
    const onMeta = () => setDuration(vid.duration || 0)
    vid.addEventListener('timeupdate', onTime)
    vid.addEventListener('loadedmetadata', onMeta)
    vid.addEventListener('durationchange', onMeta)
    if (vid.duration && isFinite(vid.duration)) setDuration(vid.duration)
    return () => {
      vid.removeEventListener('timeupdate', onTime)
      vid.removeEventListener('loadedmetadata', onMeta)
      vid.removeEventListener('durationchange', onMeta)
    }
  }, [])

  const togglePlay = useCallback(() => {
    const vid = videoRef.current
    if (!vid) return
    if (playing) vid.pause()
    else vid.play().catch(() => {})
  }, [playing])

  const seekFromClientX = (clientX: number) => {
    const track = trackRef.current
    const vid = videoRef.current
    if (!track || !vid || !duration) return
    const rect = track.getBoundingClientRect()
    const pct = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width))
    const t = pct * duration
    vid.currentTime = t
    setCurrentTime(t)
  }

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation()
    e.currentTarget.setPointerCapture(e.pointerId)
    draggingRef.current = true
    setDragging(true)
    seekFromClientX(e.clientX)
  }
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    seekFromClientX(e.clientX)
  }
  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!draggingRef.current) return
    draggingRef.current = false
    setDragging(false)
    e.currentTarget.releasePointerCapture(e.pointerId)
  }

  const pct = duration ? (currentTime / duration) * 100 : 0

  return (
    <div
      style={{
        // Full-screen scrim — blurs the page behind the centered card
        // instead of dimming it. Still catches outside clicks (and Escape)
        // to close.
        position: 'fixed', inset: 0,
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
        animation: 'svm-fade-in 0.18s ease',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        background: 'rgba(0,0,0,0.35)',
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          // TikTok's 9:16 portrait card, centered by the flex scrim above.
          // With no backdrop dim, the thick border + heavy shadow are what
          // separate the card from the page behind it.
          position: 'relative',
          aspectRatio: '9 / 16',
          height: 'min(84vh, calc(92vw * 16 / 9))',
          maxWidth: '92vw',
          background: '#000',
          borderRadius: 18,
          overflow: 'hidden',
          border: '4px solid rgba(255,255,255,0.22)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.75), 0 4px 24px rgba(0,0,0,0.5)',
        }}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute', top: 10, right: 10,
            width: 32, height: 32, borderRadius: '50%',
            background: 'rgba(0,0,0,0.45)',
            border: '1px solid rgba(255,255,255,0.22)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', zIndex: 5,
            transition: 'background 0.15s ease, transform 0.15s ease',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.22)' }}
          onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.45)' }}
        >
          <X size={16} color="white" />
        </button>
        <video
          ref={videoRef}
          src={videoStorageUrl}
          poster={thumbnailUrl ?? undefined}
          muted={muted}
          playsInline
          onClick={togglePlay}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onError={handleVideoError}
          style={{
            width: '100%', height: '100%',
            objectFit: 'contain',
            display: 'block',
            cursor: 'pointer',
            background: '#000',
          }}
        />

        <div
          onClick={e => e.stopPropagation()}
          style={{
            position: 'absolute', left: 0, right: 0, bottom: 0,
            display: 'flex', flexDirection: 'column', gap: 2,
            padding: '8px 10px 10px',
            background: 'linear-gradient(to top, rgba(0,0,0,0.78) 0%, rgba(0,0,0,0.45) 60%, transparent 100%)',
            zIndex: 4,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 2px', lineHeight: 1 }}>
            <span style={{
              fontSize: 10, color: 'white', fontWeight: 500,
              fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', opacity: 0.92,
            }}>
              {fmtTime(currentTime)} / {fmtTime(duration)}
            </span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={e => { e.stopPropagation(); togglePlay() }}
              aria-label={playing ? 'Pause' : 'Play'}
              className="svm-btn"
            >
              {playing
                ? <Pause size={14} fill="white" color="white" />
                : <Play size={14} fill="white" color="white" />}
            </button>

            <div
              ref={trackRef}
              style={{
                flex: 1, height: 18,
                display: 'flex', alignItems: 'center',
                cursor: 'pointer', position: 'relative', touchAction: 'none',
              }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <div style={{
                position: 'absolute', left: 0, right: 0,
                height: dragging ? 5 : 4, background: 'rgba(255,255,255,0.3)',
                borderRadius: 3, pointerEvents: 'none',
                transition: 'height 0.15s ease',
              }} />
              <div style={{
                position: 'absolute', left: 0,
                width: `${pct}%`,
                height: dragging ? 5 : 4, background: 'white',
                borderRadius: 3, pointerEvents: 'none',
                transition: 'height 0.15s ease',
              }} />
              <div style={{
                position: 'absolute',
                width: dragging ? 14 : 12, height: dragging ? 14 : 12,
                borderRadius: '50%', background: 'white',
                boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
                transform: 'translate(-50%, -50%)',
                top: '50%', left: `${pct}%`,
                pointerEvents: 'none',
                transition: 'width 0.15s ease, height 0.15s ease',
              }} />
            </div>

            <button
              type="button"
              onClick={e => { e.stopPropagation(); setMuted(m => !m) }}
              aria-label={muted ? 'Unmute' : 'Mute'}
              className="svm-btn"
            >
              {muted ? <VolumeX size={14} color="white" /> : <Volume2 size={14} color="white" />}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes svm-fade-in {
          from { opacity: 0 }
          to { opacity: 1 }
        }
        .svm-btn {
          display: inline-flex; align-items: center; justify-content: center;
          width: 26px; height: 26px;
          background: rgba(255,255,255,0.14);
          border: 1px solid rgba(255,255,255,0.18);
          border-radius: 50%;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          color: white;
          cursor: pointer; padding: 0; flex-shrink: 0;
          transition: background 0.15s ease, transform 0.15s ease;
        }
        .svm-btn:hover { background: rgba(255,255,255,0.26); transform: scale(1.06); }
      `}</style>
    </div>
  )
}
